import {
  Component,
  OnDestroy,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from "@angular/core";
import { DatePipe, DecimalPipe, UpperCasePipe } from "@angular/common";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { Subscription } from "rxjs";

import {
  DebugPanelService,
  DebugPanelState,
} from "../../shared/debug-panel/debug-panel.service";
import { ButtonDirective } from "primeng/button";
import { SpinnerComponent } from "../spinner/spinner.component";

@Component({
  selector: "app-debug-footer-panel",
  standalone: true,
  templateUrl: "./debug-footer-panel.component.html",
  styleUrls: ["./debug-footer-panel.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ButtonDirective,
    SpinnerComponent,
    DatePipe,
    DecimalPipe,
    UpperCasePipe,
  ],
})
export class DebugFooterPanelComponent implements OnInit, OnDestroy {
  private debugPanelService = inject(DebugPanelService);
  private sanitizer = inject(DomSanitizer);

  public isDebugMode = false;
  public debugData: any | null = null;
  public debugLoading = false;

  private subscription = new Subscription();

  public get debugPanelOpen(): boolean {
    return this.debugPanelService.debugPanelOpen;
  }

  ngOnInit(): void {
    this.subscription.add(
      this.debugPanelService.state$.subscribe((state: DebugPanelState) => {
        this.isDebugMode = state.isDebugMode;
        this.debugData = state.debugData;
        this.debugLoading = state.debugLoading;
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public togglePanel(): void {
    this.debugPanelService.debugPanelOpen =
      !this.debugPanelService.debugPanelOpen;
  }

  public formatDebugString(raw: string): SafeHtml {
    const s = raw
      .replace(
        /\{\{r:([0-9]+)\}\}\{\{ro:([0-9]+)\}\}\{\{wb:([0-9a-z]+)\}\}/gm,
        "<a href='/rate/fcl/contract/$1?route=$2&weightBracket=$3' target='_blank'><i class='pi pi-external-link'></i></a>",
      )
      .replace(
        /\{\{r:([0-9]+)\}\}/gm,
        "<a href='/rate/fcl/contract/$1' target='_blank'><i class='pi pi-external-link'></i></a>",
      )
      .replace(
        /\{\{mrqr:([0-9]+)\}\}/gm,
        "<a href='/margin-rule/rule-quote-rate/$1' target='_blank'><i class='pi pi-external-link'></i></a>",
      )
      .replace(/\{\{l:([0-9]+)\}\}/gm, "");
    return this.sanitizer.bypassSecurityTrustHtml(s);
  }

  public containerVal(
    containers: { name: string; value: number }[] | null | undefined,
    name: string,
  ): number | null {
    return containers?.find((c) => c.name === name)?.value ?? null;
  }

  public convertedVal(
    rawValue: number,
    rateCurrency: string,
    targetCurrency: string,
  ): number | null {
    const er = (this.debugData?.exchangeRates as any[])?.find(
      (e: any) => e.from === rateCurrency && e.to === targetCurrency,
    );
    if (!er) return null;
    return Math.round(
      rawValue * (er.rateWithSpread ?? er.rate * (1 + er.spread / 100)),
    );
  }

  public convertedCurrencies(rateCurrency: string): string[] {
    const ers: any[] = this.debugData?.exchangeRates ?? [];
    return [
      ...new Set<string>(
        ers
          .filter((e: any) => e.from === rateCurrency)
          .map((e: any) => e.to as string),
      ),
    ];
  }

  public relevantConvertedCurrencies(rateCurrency: string): string[] {
    const grandTotalCurrency = this.debugData?.quoteInfo?.grandTotalCurrency as
      string | undefined;
    return this.convertedCurrencies(rateCurrency).filter(
      (c) => !grandTotalCurrency || c !== grandTotalCurrency,
    );
  }

  public computeEstimatedTotal(): {
    containers: string[];
    groups: {
      rateStep: any;
      surchargeSteps: any[];
      subtotals: { name: string; value: number }[];
    }[];
  } | null {
    const totalCurrency: string = this.debugData?.quoteInfo?.totalCurrency;
    if (!totalCurrency) return null;

    const rates: any[] = this.debugData?.rates ?? [];
    const localSurcharges: any[] = this.debugData?.locationSurcharges ?? [];

    const containerNamesSet = new Set<string>();
    for (const r of rates) {
      if (r.hasRoute && r.containers) {
        for (const c of r.containers) containerNamesSet.add(c.name);
      }
    }
    if (containerNamesSet.size === 0) return null;
    const containers = [...containerNamesSet];

    const surchargeSteps: any[] = [];
    for (const ls of localSurcharges) {
      if (!ls.containers?.length) continue;
      if (
        ls.currency !== totalCurrency &&
        this.convertedVal(1, ls.currency, totalCurrency) === null
      )
        continue;
      surchargeSteps.push({
        label: `${ls.name}${ls.location ? " · " + ls.location : ""}`,
        currency: ls.currency,
        isSurcharge: true,
        values: containers.map((cn) => {
          const raw = this.containerVal(ls.containers, cn) ?? 0;
          const converted =
            ls.currency === totalCurrency
              ? raw
              : this.convertedVal(raw, ls.currency, totalCurrency);
          return { name: cn, raw, converted };
        }),
      });
    }

    const groups = rates
      .filter((r) => r.hasRoute)
      .map((r) => {
        const rateStep: any = {
          label: `${r.name}${r.carrierName ? " (" + r.carrierName + ")" : ""}`,
          currency: r.currency,
          values: containers.map((cn) => {
            const raw = this.containerVal(r.containers, cn) ?? 0;
            const converted =
              r.currency === totalCurrency
                ? raw
                : this.convertedVal(raw, r.currency, totalCurrency);
            return { name: cn, raw, converted };
          }),
        };
        const subtotals = containers.map((cn) => {
          const rateConv =
            rateStep.values.find((v: any) => v.name === cn)?.converted ?? 0;
          const surchargeConv = surchargeSteps.reduce((sum: number, s: any) => {
            return (
              sum + (s.values.find((v: any) => v.name === cn)?.converted ?? 0)
            );
          }, 0);
          return { name: cn, value: rateConv + surchargeConv };
        });
        return { rateStep, surchargeSteps, subtotals };
      });

    if (groups.length === 0) return null;
    return { containers, groups };
  }
}
